import { useEffect, useMemo, useState } from 'react';
import { notify } from '../../../app';
import {
  getAgentPrompts,
  createAgentPrompt,
  updateAgentPrompt,
  deleteAgentPrompt,
} from '../../../api/agent-prompts';
import { useFetch, useRequest } from '../../../hooks';
import type { AgentPromptDto } from '../../../api/agent-prompts';

export type PromptsFormType = {
  end_of_conversation_metadata_extractor_prompt?: string;
  conversation_metrics_prompt?: string;
  pii_masking_prompt?: string;
  system_prompt?: string;
  inprompt_knowledge_base?: string;
};

const defaultFormValues: PromptsFormType = {
  end_of_conversation_metadata_extractor_prompt: '',
  conversation_metrics_prompt: '',
  pii_masking_prompt: '',
  system_prompt: '',
  inprompt_knowledge_base: '',
};

export const useAgentPrompts = (agentId: string) => {
  const {
    intializing: loadingPrompts,
    data: prompts,
    refresh: refreshPrompts,
  } = useFetch(() => getAgentPrompts(agentId), [agentId]);

  const loading = loadingPrompts;

  const promptsByName = useMemo(() => {
    const map = new Map<string, AgentPromptDto>();
    prompts?.forEach(prompt => {
      map.set(prompt.name, prompt);
    });
    return map;
  }, [prompts]);

  const initialFormValues = useMemo((): PromptsFormType => {
    if (!prompts || prompts.length === 0) {
      return defaultFormValues;
    }

    return {
      end_of_conversation_metadata_extractor_prompt:
        promptsByName.get('end_of_conversation_metadata_extractor_prompt')
          ?.prompt || '',
      conversation_metrics_prompt:
        promptsByName.get('conversation_metrics_prompt')?.prompt || '',
      pii_masking_prompt: promptsByName.get('pii_masking_prompt')?.prompt || '',
      system_prompt: promptsByName.get('system_prompt')?.prompt || '',
      inprompt_knowledge_base:
        promptsByName.get('inprompt_knowledge_base')?.prompt || '',
    };
  }, [promptsByName, prompts]);

  const [formValues, setFormValues] =
    useState<PromptsFormType>(initialFormValues);
  const [formKey, setFormKey] = useState(0);

  const [savePrompt, savingPrompt] = useRequest(
    async (
      _signal,
      promptName: string,
      promptValue: string
    ): Promise<'saved' | 'deleted' | 'noop'> => {
      const existingPrompt = prompts?.find(p => p.name === promptName);
      const trimmedPrompt = promptValue.trim();

      if (!trimmedPrompt) {
        if (existingPrompt?.id) {
          await deleteAgentPrompt(agentId, existingPrompt.id);
          return 'deleted';
        }
        return 'noop';
      }

      if (existingPrompt?.id) {
        await updateAgentPrompt(agentId, existingPrompt.id, {
          prompt: trimmedPrompt,
        });
      } else {
        await createAgentPrompt(agentId, {
          name: promptName,
          prompt: trimmedPrompt,
        });
      }
      return 'saved';
    },
    {
      deps: [agentId, prompts],
      onSuccess: action => {
        if (action === 'deleted') {
          notify.success('Prompt deleted successfully');
        } else if (action === 'saved') {
          notify.success('Prompt saved successfully');
        }
        refreshPrompts?.();
      },
      onError: () => {
        notify.error('Failed to save prompt');
        refreshPrompts?.();
      },
    }
  );

  useEffect(() => {
    setFormValues(initialFormValues);
    setFormKey(prev => prev + 1);
  }, [initialFormValues]);

  const updating = savingPrompt;

  return {
    loading,
    updating,
    promptsByName,
    initialFormValues,
    formValues,
    formKey,
    setFormValues,
    savePrompt,
  };
};
